import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLine } from "../components/Reveal";
import { PhoneShot } from "../components/PhoneShot";
import { Lancamento } from "../components/Lancamento";
import { CTA_URL } from "../constants/site";

const PrimaryCTA = ({ children, testId }) => (
  <a
    href={CTA_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="group inline-flex items-center gap-2 rounded-full bg-salon px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-salon-deep"
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
  </a>
);

const SectionLink = ({ href, children, testId }) => (
  <a
    href={href}
    data-testid={testId}
    className="group inline-flex items-center gap-1.5 text-sm font-medium text-salon transition-colors hover:text-salon-deep"
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);

const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-salon">{children}</p>
);

const Abertura = () => (
  <section id="inicio" data-testid="abertura-section" className="relative mt-[68px] bg-charcoal md:h-[calc(100svh-68px)] md:overflow-hidden">
    <picture>
      <source media="(max-width: 767px)" srcSet="/images/abertura-celular.webp" />
      <img
        src="/images/abertura-computador.webp"
        alt="Profissional de salão sentada com um tablet, cercada por cartões de Agenda, Clientes, Comercial, Gestão e Operação"
        data-testid="abertura-imagem"
        className="h-auto w-full md:h-full md:object-cover md:object-center"
        fetchpriority="high"
      />
    </picture>
    <a
      href="#apresentacao"
      aria-label="Rolar para a apresentação"
      data-testid="abertura-seta"
      className="absolute bottom-6 right-6 rounded-full p-2 text-white transition-transform duration-300 hover:translate-y-1 md:bottom-9 md:right-9"
    >
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 4v16" />
        <path d="m6 14 6 6 6-6" />
      </svg>
    </a>
  </section>
);

const PERGUNTAS = [
  "O que está acontecendo no meu salão?",
  "O que isso significa?",
  "O que eu faço agora?",
];

const Apresentacao = () => {
  return (
    <section id="apresentacao" data-testid="apresentacao-section" className="relative overflow-hidden bg-white">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid items-center gap-14 py-20 md:min-h-[calc(100svh-68px)] md:grid-cols-2 md:gap-20 md:py-16">
          <div>
            <Reveal y={12}>
              <Eyebrow>Gestão para o negócio da beleza</Eyebrow>
            </Reveal>
            <h1 className="mt-7 font-display text-[2.4rem] font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-[3.6rem]">
              <MaskedLine delay={0.1}>Seu salão trabalha todos os dias.</MaskedLine>
              <MaskedLine delay={0.25}>
                <span className="text-salon">Sua gestão também deveria.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.35}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-charcoal/65 md:text-lg">
                Existimos para dar clareza a quem constrói negócios através da beleza.{" "}
                <span className="font-medium text-charcoal">
                  Você não precisa virar administrador. A gestão é que precisa caber na sua rotina.
                </span>
              </p>
            </Reveal>
            <Reveal delay={0.45} className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href="#lancamento"
                data-testid="apresentacao-cta"
                className="inline-flex items-center rounded-full bg-salon px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-salon-deep"
              >
                Quero ser avisado do lançamento
              </a>
              <a
                href="#produto"
                data-testid="apresentacao-link-produto"
                className="text-sm font-medium text-charcoal underline decoration-charcoal/30 underline-offset-4 transition-colors hover:decoration-charcoal"
              >
                Ver como funciona
              </a>
            </Reveal>
            <Reveal delay={0.55} className="mt-12 border-t border-charcoal/10 pt-6">
              <p className="max-w-md text-balance text-[21px] font-medium leading-snug text-charcoal md:text-[22px]">
                Criado dentro de um salão, por quem vive da beleza há mais de 25 anos.
              </p>
            </Reveal>
          </div>

          <div>
            <Reveal>
              <p className="text-sm text-charcoal/55">O SalonUP existe para responder a três perguntas:</p>
            </Reveal>
            <div className="mt-6 space-y-4">
              {PERGUNTAS.map((q, i) => (
                <Reveal key={q} delay={0.15 + i * 0.08} className={i === 1 ? "md:ml-10" : i === 2 ? "md:ml-20" : ""}>
                  <div
                    data-testid={`pergunta-card-${i + 1}`}
                    className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-[0_18px_45px_-24px_rgba(31,31,31,0.22)] ring-1 ring-charcoal/[0.06]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-salon-mist font-display text-sm font-semibold text-salon">
                      {i + 1}
                    </span>
                    <p className="text-base font-medium leading-snug text-charcoal">{q}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

const TELAS_PROBLEMA = [
  {
    src: "/screenshots/clientes.png",
    alt: "Tela real do módulo Clientes da SalonUP — ficha viva com histórico e vínculo do cliente",
    destaque: "Seus clientes, com histórico",
    nome: "Clientes",
    testId: "problema-tela-clientes",
    offset: "md:pt-10",
  },
  {
    src: "/screenshots/gestao-core.png",
    alt: "Tela real da Gestão SalonUP — módulos Clientes, PAC MAN, COD e Comandas",
    destaque: "Tudo em um só lugar",
    nome: "Gestão",
    testId: "problema-tela-gestao",
    offset: "",
  },
  {
    src: "/screenshots/cod.png",
    alt: "Tela do COD do SalonUP com a disponibilidade da equipe no dia",
    destaque: "A agenda começa pela disponibilidade de cada profissional",
    nome: "Disponibilidade (COD)",
    testId: "problema-tela-cod",
    offset: "md:pt-16",
  },
];

const Problema = () => (
  <section data-testid="problema-section" className="bg-white">
    <div className="mx-auto max-w-6xl px-6">
      <div className="grid items-center gap-14 py-20 md:min-h-[calc(100svh-68px)] md:grid-cols-[1fr_1.35fr] md:gap-16 md:py-16">
        <div>
          <Reveal>
            <Eyebrow>O problema</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight text-charcoal sm:text-4xl lg:text-[3.2rem] text-balance">
              Movimento não é a mesma coisa que gestão.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base leading-relaxed text-charcoal/65 md:text-lg">
              Um salão pode estar cheio e ainda assim não saber se está crescendo. Sem registro,
              tudo fica na memória, e a decisão vira impressão.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-8">
            <a
              href="#gestao"
              data-testid="problema-link-modulos"
              className="text-sm font-medium text-charcoal underline decoration-charcoal/30 underline-offset-4 transition-colors hover:decoration-charcoal"
            >
              Conhecer os módulos
            </a>
          </Reveal>
        </div>

        <div>
          <Reveal>
            <div
              data-testid="problema-selo"
              className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-[0_18px_45px_-24px_rgba(31,31,31,0.22)] ring-1 ring-charcoal/[0.06]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-salon-mist font-display text-sm font-semibold text-salon">
                1
              </span>
              <p className="text-base font-medium leading-snug text-charcoal">
                O que está acontecendo no meu salão?
              </p>
            </div>
          </Reveal>

          <div className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:snap-none md:grid-cols-3 md:items-start md:gap-6 md:overflow-visible md:pb-0">
            {TELAS_PROBLEMA.map((tela, i) => (
              <Reveal
                key={tela.nome}
                delay={0.1 + i * 0.1}
                className={`w-[64%] shrink-0 snap-start md:w-auto ${tela.offset}`}
              >
                <figure data-testid={tela.testId}>
                  <div className="overflow-hidden rounded-[1.5rem] border border-charcoal/10 bg-white shadow-[0_28px_70px_-32px_rgba(31,31,31,0.25)]">
                    <img src={tela.src} alt={tela.alt} loading="lazy" className="block h-auto w-full" />
                  </div>
                  <figcaption className="mt-4 text-center">
                    <span className="block text-sm font-medium text-charcoal">{tela.destaque}</span>
                    <span className="mt-1 block text-xs text-charcoal/50">{tela.nome}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MARQUEE_ITEMS = ["Clientes", "Operação", "Gestão", "Comercial", "Resultados"];

const Marquee = () => {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="relative overflow-hidden py-4" aria-hidden="true">
      <div className="marquee-track flex w-max animate-marquee items-center">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center">
                <span className="whitespace-nowrap px-8 font-display text-4xl font-light tracking-tight text-charcoal/25 md:px-14 md:text-6xl">
                  {item}
                </span>
                <span className="h-2 w-2 rounded-full bg-salon/40" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const Ideia = () => (
  <section data-testid="ideia-section" className="border-y border-charcoal/[0.06] bg-neutral-light py-28 md:py-40">
    <div className="mx-auto max-w-6xl px-6">
      <div className="mx-auto max-w-2xl text-center">
        <Reveal>
          <Eyebrow>A ideia SalonUP</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight text-charcoal sm:text-4xl lg:text-[3.2rem]">
            Seu salão em um só lugar.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base leading-relaxed text-charcoal/65 md:text-lg">
            <span className="md:block">O que acontece no salão vira informação.</span>{" "}
            <span className="md:block">A informação vira gestão.</span>
          </p>
        </Reveal>
      </div>
    </div>
    <Reveal delay={0.15} className="mt-16 md:mt-20">
      <Marquee />
    </Reveal>
  </section>
);

const Produto = () => (
  <section id="produto" data-testid="produto-section" className="py-28 md:py-44">
    <div className="mx-auto max-w-6xl px-6">
      <div className="max-w-2xl">
        <Reveal>
          <Eyebrow>O produto</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight text-charcoal sm:text-4xl lg:text-[3.2rem] text-balance">
            Uma plataforma. Não uma promessa.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base leading-relaxed text-charcoal/65 md:text-lg">
            Telas reais, construídas para a rotina real de um salão. Cada acontecimento da
            operação deixa um rastro que a gestão consegue ler.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 space-y-24 md:mt-24 md:space-y-32">
        <div className="grid items-center gap-10 md:grid-cols-[1.2fr_1fr] md:gap-16">
          <Reveal>
            <PhoneShot
              src="/screenshots/comandas.png"
              alt="Tela real de Comandas — serviços e produtos registrados antes do recebimento no caixa"
              label="Comandas"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-display text-2xl font-medium tracking-tight text-charcoal md:text-3xl text-balance">
              A operação registrada antes do dinheiro.
            </h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/65">
              Múltiplos serviços e produtos por comanda, ligados a cliente e profissional. O
              estoque baixa apenas no recebimento do caixa — execução, estoque e financeiro
              deixam de ser três histórias separadas.
            </p>
          </Reveal>
        </div>

        <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
          <Reveal className="order-2 md:order-1">
            <h3 className="font-display text-2xl font-medium tracking-tight text-charcoal md:text-3xl text-balance">
              O dia financeiro, legível de ponta a ponta.
            </h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-charcoal/65">
              Abertura, movimentações e fechamento em um fluxo contínuo, com responsável e
              registro. O caixa sempre abre zerado — clareza em vez de achismo.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="order-1 md:order-2">
            <PhoneShot
              src="/screenshots/caixa.png"
              alt="Tela real do Caixa — movimentação financeira do dia com abertura padronizada"
              label="Caixa"
            />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const GESTAO_EXEMPLOS = [
  { nome: "Clientes", desc: "Cadastro, ficha viva, histórico técnico e vínculo com o cliente." },
  { nome: "PAC MAN", desc: "Estoque, movimentações, compras e custo real por serviço." },
  { nome: "Tarefas", desc: "Lembretes, compromissos e pendências — pendentes, atrasadas, urgentes, concluídas." },
];

const Gestao = () => (
  <section id="gestao" data-testid="gestao-section" className="bg-charcoal py-28 text-white md:py-40">
    <div className="mx-auto max-w-6xl px-6">
      <div className="max-w-2xl">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/50">Gestão</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight sm:text-4xl lg:text-[3.2rem] text-balance">
            O que acontece no salão precisa deixar rastros.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base leading-relaxed text-white/65 md:text-lg">
            Clientes, COD, comandas, serviços, caixa, AFO, tarefas e produção — áreas reais da
            operação, registradas onde acontecem.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid items-center gap-12 md:mt-20 md:grid-cols-2 md:gap-16">
        <Reveal>
          <img
            src="/images/gestao-salao-celular.webp"
            alt="Profissional de salão olhando o celular, com cartões de novo agendamento, cliente em retorno, comanda fechada, estoque baixo e faturamento do dia"
            loading="lazy"
            data-testid="gestao-imagem-profissional"
            className="w-full rounded-3xl ring-1 ring-white/10"
          />
        </Reveal>
        <div className="space-y-4">
          {GESTAO_EXEMPLOS.map((item, i) => (
            <Reveal
              key={item.nome}
              delay={i * 0.1}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-7 md:p-8"
            >
              <h3 className="font-display text-xl font-medium tracking-tight text-white">{item.nome}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-white/55">{item.desc}</p>
            </Reveal>
          ))}
          <Reveal delay={0.3} className="pt-3">
            <a
              href="#gestao"
              data-testid="gestao-cta"
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-white/85 transition-colors hover:text-white"
            >
              Conhecer Gestão
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Comercial = () => (
  <section id="comercial" data-testid="comercial-section" className="py-28 md:py-40">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 md:grid-cols-2 md:gap-20">
      <div>
        <Reveal>
          <Eyebrow>Comercial — CONNECT</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight text-charcoal sm:text-4xl lg:text-[3.2rem] text-balance">
            Não basta gerar oportunidades.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base leading-relaxed text-charcoal/65 md:text-lg">
            É preciso acompanhar relacionamento, conversas e conversões. O CONNECT organiza
            leads, campanhas e recorrência para que nenhuma conversa importante se perca.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10">
          <SectionLink href="#comercial" testId="comercial-cta">
            Conhecer Comercial
          </SectionLink>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="mx-auto w-full max-w-md">
        <PhoneShot
          src="/screenshots/connect.png"
          alt="Tela real do ecossistema SalonUP — card do CONNECT, área comercial para leads, campanhas e relacionamento"
          label="CONNECT · em breve"
        />
      </Reveal>
    </div>
  </section>
);

const Inteligencia = () => (
  <section id="inteligencia" data-testid="inteligencia-section" className="bg-charcoal py-28 text-white md:py-40">
    <div className="mx-auto max-w-6xl px-6">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/50">Inteligência · em breve</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-medium leading-[1.12] tracking-tight sm:text-4xl lg:text-[3.2rem] text-balance">
            O que você não enxerga, você não consegue gerir.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
            Os dados produzidos pela operação existem para ajudar decisões. Leitura, indicadores
            e contexto — do dado ao entendimento, do entendimento à decisão.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10">
          <a
            href="#inteligencia"
            data-testid="inteligencia-cta"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-white/85 transition-colors hover:text-white"
          >
            Conhecer Inteligência
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const Fechamento = () => (
  <section data-testid="fechamento-section" className="py-32 md:py-48">
    <div className="mx-auto grid max-w-6xl items-center gap-16 px-6 md:grid-cols-[1.1fr_1fr] md:gap-20">
      <div className="text-center md:text-left">
        <Reveal>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-charcoal sm:text-5xl lg:text-[3.9rem] text-balance">
            Um salão atende. <span className="text-salon">Um negócio entende seus números.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-12">
          <PrimaryCTA testId="fechamento-cta">Conhecer a SalonUP</PrimaryCTA>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="mx-auto w-full max-w-md md:max-w-none">
        <img
          src="/images/profissional-ruiva.png"
          alt="Profissional da beleza com smartphone, representando a rotina conectada do salão"
          loading="lazy"
          data-testid="fechamento-imagem-profissional"
          className="w-full"
        />
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Abertura />
        <Apresentacao />
        <Problema />
        <Ideia />
        <Produto />
        <Gestao />
        <Comercial />
        <Inteligencia />
        <Fechamento />
      </main>
      <Lancamento />
      <Footer />
    </div>
  );
}
